import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { section as sectionData } from "shared/data";
import { useCurrentPageType } from "shared/hooks";
import type { Edition } from "shared/lib/edition";
import { PageType } from "shared/lib/page-type";

import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { DefaultHeaderSwapThreshold } from "scmp-app/components/header/consts";
import { useHeaderFeaturesProvider } from "scmp-app/components/header/features/hooks";
import { useSwapOnScrollEffect } from "scmp-app/components/header/hooks";
import {
  getArticleLayout,
  getDefaultLayout,
  getHomeMiniLayout,
  getStyleArticleLayout,
  getStyleLayout,
} from "scmp-app/components/header/layouts";
import {
  Container,
  HeaderContainer,
  LeftFeatureContainer,
  RightFeatureContainer,
  StyleHeaderLogoContainer,
} from "scmp-app/components/header/styles";
import type { HeaderConfiguration } from "scmp-app/components/header/types";
import { HeaderLogo } from "scmp-app/components/section/section-style/styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
  initEdition?: Edition;
  isHomeMiniHeader?: boolean;
};

type HeaderState = "article" | "default" | "homeMiniHeader" | "style" | "styleArticle";

// Helper Hook to keep main component clean
const useHeaderState = (
  headerConfiguration: HeaderConfiguration | undefined,
  isHomeMiniHeader: boolean | undefined,
) => {
  const { query } = useRouter();
  const currentPageType = useCurrentPageType();
  const { reference } = useAppBarSetStickyScrollHeight();

  const isArticlePage = currentPageType === PageType.Article;
  const isSectionStyleMainPage =
    currentPageType === PageType.Section && query.entityId === sectionData.style.entityId;
  const isMagazineStyle = headerConfiguration?.appBarVariant === "scmp/magazines-style";

  const { shouldSwap } = useSwapOnScrollEffect(
    (headerConfiguration?.appBarStickyScrollHeight ?? 0) || DefaultHeaderSwapThreshold,
  );

  const headerState: HeaderState = useMemo(() => {
    if (isMagazineStyle) {
      return isArticlePage ? "styleArticle" : "style";
    }
    if (isArticlePage) return "article";
    if (isHomeMiniHeader) return "homeMiniHeader";
    return "default";
  }, [isArticlePage, isMagazineStyle, isHomeMiniHeader]);

  return {
    headerState,
    isMagazineStyle,
    isSectionStyleMainPage,
    reference,
    shouldSwap,
  };
};

export const Header: FunctionComponent<Props> = ({
  className,
  headerConfiguration,
  initEdition,
  isHomeMiniHeader,
}) => {
  const { headerState, isMagazineStyle, isSectionStyleMainPage, reference, shouldSwap } =
    useHeaderState(headerConfiguration, isHomeMiniHeader);

  const features = useHeaderFeaturesProvider();

  const layout = useMemo(() => {
    const props = {
      config: headerConfiguration,
      features,
      initEdition,
      shouldSwap,
    };

    switch (headerState) {
      case "article":
        return getArticleLayout(props);
      case "homeMiniHeader":
        return getHomeMiniLayout(props);
      case "style":
        return getStyleLayout(props);
      case "styleArticle":
        return getStyleArticleLayout(props);
      default:
        return getDefaultLayout(props);
    }
  }, [headerState, features, shouldSwap, headerConfiguration, initEdition]);

  return (
    <Container $isMagazineStyle={isMagazineStyle} className={className}>
      {isSectionStyleMainPage && (
        <StyleHeaderLogoContainer ref={reference}>
          <HeaderLogo />
        </StyleHeaderLogoContainer>
      )}
      <HeaderContainer>
        <LeftFeatureContainer>
          <ClientSideSuspense>{layout.left}</ClientSideSuspense>
        </LeftFeatureContainer>

        {/* Note: If Center contains dynamic/user data, wrap it in ClientSideSuspense too */}
        {layout.center}

        <RightFeatureContainer $shouldSwap={headerState === "article" && shouldSwap}>
          <ClientSideSuspense>{layout.right}</ClientSideSuspense>
        </RightFeatureContainer>
      </HeaderContainer>
    </Container>
  );
};

Header.displayName = "Header";
